import Alert from 'antd/es/alert';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Col from 'antd/es/col';
import DatePicker from 'antd/es/date-picker';
import Descriptions from 'antd/es/descriptions';
import Form from 'antd/es/form';
import Row from 'antd/es/row';
import Select from 'antd/es/select';
import Spin from 'antd/es/spin';
import { previewBulkReconciliation } from 'api/incidence.api';
import { getActiveContractKinds } from 'api/contract.api';
import { PageTitle } from 'components/common/PageTitle/PageTitle';
import { H1 } from 'components/common/typography/H1/H1';
import { incidencePaths, usersPaths } from 'constants/routePaths';
import dayjs, { Dayjs } from 'dayjs';
import { getStartOfDate } from 'hooks/getStartEndOfDate';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ContractKind } from 'types/contract';
import {
  BulkReconciliationPayload,
  BulkReconciliationPreview,
} from 'types/incidences';
import styles from 'components/incidences/ReconcileIncidences.module.scss';

import {
  isCompletePreview,
  useReconciliationQueue,
} from './hooks/useReconciliationQueue';
import { ReconciliationProgress } from './components/ReconciliationProgress';
import { ReconciliationNavigationGuard } from './components/ReconciliationNavigationGuard';

interface BulkReconciliationFormValues {
  contractKind: string;
  startDate: Dayjs;
}

interface BulkReconciliationNavigationState {
  contractKindId?: string;
  applicationDate?: string;
}

const BulkReconciliationContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState =
    location.state as BulkReconciliationNavigationState | null;
  const fromImport = Boolean(
    navigationState?.contractKindId && navigationState.applicationDate
  );
  const [form] = Form.useForm<BulkReconciliationFormValues>();
  const [contractKinds, setContractKinds] = useState<ContractKind[]>([]);
  const [loadingKinds, setLoadingKinds] = useState(true);
  const [preview, setPreview] = useState<BulkReconciliationPreview>();
  const [payload, setPayload] = useState<BulkReconciliationPayload>();
  const queue = useReconciliationQueue();
  const previewRequest = useRef(0);
  const previewBusy = useRef(false);
  const previewHeading = useRef<HTMLHeadingElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    getActiveContractKinds()
      .then(({ data }) => {
        if (active) setContractKinds(data);
      })
      .catch(() => {
        if (active) setError('No fue posible cargar los tipos de contrato.');
      })
      .finally(() => {
        if (active) setLoadingKinds(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!navigationState?.contractKindId || !navigationState.applicationDate)
      return;
    form.setFieldsValue({
      contractKind: navigationState.contractKindId,
      startDate: dayjs(navigationState.applicationDate),
    });
  }, [form, navigationState?.applicationDate, navigationState?.contractKindId]);

  const contractKindOptions = useMemo(
    () => contractKinds.map((kind) => ({ label: kind.name, value: kind._id })),
    [contractKinds]
  );

  useEffect(() => {
    previewHeading.current?.focus();
  }, [preview]);
  useEffect(
    () => () => {
      previewRequest.current += 1;
    },
    []
  );

  const onPreview = async (values: BulkReconciliationFormValues) => {
    if (previewBusy.current || queue.status !== 'idle') return;
    previewBusy.current = true;
    const request = ++previewRequest.current;
    setPreview(undefined);
    setPayload(undefined);
    const nextPayload: BulkReconciliationPayload = {
      contractKind: values.contractKind,
      applicationDate: getStartOfDate(values.startDate),
      origin: fromImport ? 'import' : 'list',
    };
    setLoading(true);
    setError('');
    try {
      const { data } = await previewBulkReconciliation(nextPayload);
      if (request !== previewRequest.current) return;
      if (
        !isCompletePreview(data) ||
        data.contractKind.id !== nextPayload.contractKind ||
        data.applicationDate !== nextPayload.applicationDate
      ) {
        throw new Error('Incomplete preview');
      }
      setPayload(nextPayload);
      setPreview(data);
    } catch {
      if (request !== previewRequest.current) return;
      setError(
        'No fue posible obtener la vista previa. Revisa los datos e inténtalo de nuevo.'
      );
    } finally {
      previewBusy.current = false;
      if (request === previewRequest.current) setLoading(false);
    }
  };

  return (
    <div id="bulk-reconcile-incidences">
      <PageTitle>Conciliar incidencias por tipo de contrato</PageTitle>
      <H1 className={styles.title}>
        Conciliar incidencias por tipo de contrato
      </H1>
      {error && (
        <Alert type="error" showIcon message={error} className="mb-3" />
      )}
      {queue.status === 'idle' && (
        <Spin spinning={loading} tip="Cargando vista previa...">
          <Card>
            <p>
              Selecciona el tipo de contrato y la fecha inicial. El tipo define
              los empleados incluidos; el cálculo conserva las reglas de la
              conciliación individual de cada empleado.
            </p>
            <Form
              form={form}
              layout="vertical"
              onFinish={onPreview}
              disabled={loading}
              onValuesChange={() => {
                previewRequest.current += 1;
                setPreview(undefined);
                setPayload(undefined);
              }}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Tipo de contrato"
                    name="contractKind"
                    rules={[
                      {
                        required: true,
                        message: 'Selecciona un tipo de contrato.',
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder="Selecciona un tipo de contrato"
                      options={contractKindOptions}
                      loading={loadingKinds}
                      disabled={loadingKinds || loading}
                      aria-label="Tipo de contrato"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Fecha de inicio"
                    name="startDate"
                    rules={[
                      {
                        required: true,
                        message: 'Selecciona la fecha de inicio.',
                      },
                    ]}
                  >
                    <DatePicker
                      className={styles.datePicker}
                      disabled={loading}
                      aria-label="Fecha de inicio"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={loadingKinds}
              >
                Ver vista previa
              </Button>
            </Form>
            {preview && (
              <Card
                type="inner"
                title={
                  <h2 ref={previewHeading} tabIndex={-1}>
                    Vista previa
                  </h2>
                }
                className="mt-4"
              >
                <Descriptions bordered size="small" column={1}>
                  <Descriptions.Item label="Tipo de contrato">
                    {preview.contractKind.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Fecha de inicio">
                    {preview.applicationDate}
                  </Descriptions.Item>
                  <Descriptions.Item label="Empleados elegibles">
                    {preview.employeeCount}
                  </Descriptions.Item>
                </Descriptions>
                {preview.employeeCount === 0 ? (
                  <Alert
                    className="mt-3"
                    type="info"
                    showIcon
                    message="No hay empleados elegibles; no se realizará ninguna conciliación."
                  />
                ) : (
                  <div className="mt-3">
                    <Button
                      type="primary"
                      onClick={() => payload && queue.start(preview, payload)}
                      loading={loading}
                    >
                      Confirmar y conciliar incidencias
                    </Button>
                  </div>
                )}
              </Card>
            )}
          </Card>
        </Spin>
      )}
      <ReconciliationNavigationGuard
        enabled={queue.shouldWarn}
        onLeave={queue.abandon}
      />
      {queue.status !== 'idle' && preview && (
        <ReconciliationProgress
          queue={queue}
          preview={preview}
          onBack={() => navigate(usersPaths.basePath)}
        />
      )}
      {queue.status === 'idle' && !fromImport && (
        <Button
          className="mt-3"
          type="link"
          onClick={() => navigate(usersPaths.basePath)}
        >
          Volver a empleados
        </Button>
      )}
      {queue.status === 'idle' && fromImport && (
        <Button
          className="mt-3"
          type="link"
          disabled={loading}
          onClick={() => {
            form.resetFields();
            setPreview(undefined);
            setPayload(undefined);
            navigate(
              `${incidencePaths.basePath}/${incidencePaths.reconcileBulk}`,
              { replace: true, state: null }
            );
          }}
        >
          Limpiar datos de la importación
        </Button>
      )}
    </div>
  );
};

const BulkReconciliationPage = () => {
  const location = useLocation();
  // An accepted navigation to this same route must also discard the old queue.
  return <BulkReconciliationContent key={location.key} />;
};

export default BulkReconciliationPage;
